import { Button, Drawer, Modal, Spin } from 'antd';
import React from 'react';
import { EMeetingUserType, TServerMeetingRoomUser, TServerRoomInfo } from 'types/zoom';
import s from './s.module.less';
import { meetingKick } from 'api/zoom';
import { getMeetingUuid } from 'utils/sessionStorage';

type Props = {
    open: boolean;
    onCancel: () => void;
    onConfirm: () => void;
    onMeetingInfoUpdated: () => void;
    role?: EMeetingUserType;
    meetingInfo?: TServerRoomInfo;
};

//not same ui with antd
const ParticipantsDrawer = (props: Props) => {
    const { open, onCancel, onConfirm, onMeetingInfoUpdated, meetingInfo, role = EMeetingUserType.PROVIDER } = props;

    const [me, setMe] = React.useState<TServerMeetingRoomUser | undefined>();
    const [others, setOthers] = React.useState<TServerMeetingRoomUser[]>([]);
    const [pendingRemoveUser, setPendingRemoveUser] = React.useState<TServerMeetingRoomUser | undefined>();
    const [showRemoveConfirmModal, setShowRemoveConfirmModal] = React.useState(false);
    const [removingUuid, setRemovingUuid] = React.useState<string | undefined>();
    const kickedUuids = React.useRef<string[]>([]);

    React.useEffect(() => {
        if (meetingInfo && meetingInfo.meetingRoomUsers) {
            const users = meetingInfo.meetingRoomUsers;
            if (role === EMeetingUserType.PROVIDER) {
                setMe(users.find((user) => user.userType === EMeetingUserType.PROVIDER));
                setOthers(users.filter((user) => user.userType === EMeetingUserType.KLARITY_USER));
            } else {
                setMe(users.find((user) => user.userId === meetingInfo.currentUserId));
                const _others: TServerMeetingRoomUser[] = [];
                const addUserId: number[] = [];
                users.forEach((user) => {
                    if (user.userId !== meetingInfo.currentUserId) {
                        if (!addUserId.includes(user.userId)) {
                            addUserId.push(user.userId);
                            _others.push(user);
                        }
                    }
                });
                setOthers(_others);
            }
        }
    }, [meetingInfo, role]);

    const handleOk = async () => {
        if (!pendingRemoveUser) {
            return;
        }
        setRemovingUuid(pendingRemoveUser.userUuid);
        const res = await meetingKick(meetingInfo?.sessionKey!, getMeetingUuid()!, `${pendingRemoveUser.userId}`);
        if (!res.error) {
            kickedUuids.current.push(pendingRemoveUser.userUuid);
            setShowRemoveConfirmModal(false);
            setPendingRemoveUser(undefined);
            onMeetingInfoUpdated?.();
        }
        setRemovingUuid(undefined);
    };

    return (
        <div>
            <Drawer
                open={open}
                onClose={onCancel}
                title={
                    <div className={s.header}>
                        <span onClick={onCancel} className={s.close}>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                            >
                                <path
                                    d="M6 18L18 6M6 6L18 18"
                                    stroke="#9CA3AF"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span className={s.title}>Participants</span>
                    </div>
                }
                width={400}
                placement="right"
                rootClassName={s.drawerRoot}
                closeIcon={null}
                className={s.drawer}
            >
                <div className={s.provider}>
                    {me && (
                        <div className={s.me}>
                            {me.firstName} {me.lastName} (Me)
                        </div>
                    )}
                    {others && others.length > 0 && (
                        <div className={s.otherList}>
                            {others.map((user) => {
                                if (kickedUuids.current.includes(user.userUuid)) {
                                    return null;
                                }
                                return (
                                    <div className={s.otherItem} key={user.userId}>
                                        <div className={s.name}>
                                            {user.firstName} {user.lastName}
                                        </div>
                                        <div
                                            onClick={() => {
                                                setPendingRemoveUser(user);
                                                setShowRemoveConfirmModal(true);
                                            }}
                                            className={s.remove}
                                        >
                                            Remove
                                            {removingUuid === user.userUuid && <Spin />}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <Modal
                        open={showRemoveConfirmModal}
                        onCancel={() => {
                            setShowRemoveConfirmModal(false);
                            setPendingRemoveUser(undefined);
                        }}
                        title="Remove participant"
                        footer={
                            <div className={s.footerWrap}>
                                <div className={s.removeFooter}>
                                    <Button
                                        loading={removingUuid === pendingRemoveUser?.userUuid}
                                        type="primary"
                                        danger
                                        size="large"
                                        className={s.removeBtn}
                                        onClick={handleOk}
                                    >
                                        Remove
                                    </Button>
                                    <Button
                                        onClick={() => {
                                            setShowRemoveConfirmModal(false);
                                            setPendingRemoveUser(undefined);
                                        }}
                                        size="large"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        }
                    >
                        <div className={s.removeContent}>
                            Once removed, the participant, {pendingRemoveUser?.firstName} {pendingRemoveUser?.lastName},
                            will be disconnected from the call and may rejoin if needed.
                        </div>
                    </Modal>
                </div>
            </Drawer>
        </div>
    );
};

export default ParticipantsDrawer;
